import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@hiiretail/react-foundation-ui';
import { useAppDispatch } from 'app/store/store';
import { DialogFormData } from 'common/receipt.types';
import { clearSelectedItem, getSelectedItem, getSelectedItemIndex } from 'features/itemSlice';
import { useStepper } from 'hooks/useStepper';
import { FC, useCallback, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { FormValues } from './addEditItem.typings';
import { ItemFormStep } from './ItemFormStep';
import { StepperComponent } from './Stepper';
import { useIsEditMode } from './useCheckType';
import { EcoTaxLoadingPlaceholder } from './EcoTaxLoadingPlaceholder';
import { useEcoTax } from './useEcoTax';

interface AddItemFormDialogProps {
  onAppendItem: (item: DialogFormData) => void;
  onEditItem: (updatedItem: any, index: number) => void;
  invoiceDate: string;
}

const styles = {
  dialog: {
    '& .MuiDialogContent-root': {
      padding: '0 32px 0',
    },
    '& .MuiDialog-paper': {
      maxWidth: 'none',
    },
    '& .MuiDialog-container': {
      alignItems: 'flex-start',
    },
  },
  actions: {
    p: 4,
  },
  button: {
    textTransform: 'uppercase',
    boxShadow: 'none',
  },
};

export const AddEditItemFormDialog: FC<AddItemFormDialogProps> = ({
  onAppendItem,
  onEditItem,
  invoiceDate,
}) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const item = useSelector(getSelectedItem);
  const itemIndex = useSelector(getSelectedItemIndex);
  const isEditMode = useIsEditMode(item);

  // Determine eco tax steps (one step per deposit/fee rule)
  const depositAndFeeRuleIds = useMemo(
    () => item?.depositAndFeeRuleIds || [],
    [item?.depositAndFeeRuleIds]
  );
  const ecoTaxStepsCount = depositAndFeeRuleIds.length;

  const { activeStep, goToStep, setActiveStep } = useStepper(ecoTaxStepsCount + 1); // Step 0: Parent; steps 1..N: eco tax items

  const { isLoading, ecoTaxData, isSuccess, isError } = useEcoTax({
    condition: !!item && ecoTaxStepsCount > 0,
    invoiceDate,
  });

  // Build step labels dynamically
  const steps = useMemo(() => {
    if (ecoTaxStepsCount === 0) return [];
    
    const parentLabel = intl.formatMessage({ id: 'receipt.item.step.parent' });
    const ecoTaxLabels = Array.from({ length: ecoTaxStepsCount }, (_, i) =>
      isSuccess && Array.isArray(ecoTaxData) && ecoTaxData[i]?.name
        ? intl.formatMessage({ id: 'receipt.item.step.ecoTax' }, { name: ecoTaxData[i].name })
        : intl.formatMessage({ id: 'receipt.item.step.ecoTax.loading' })
    );
    return [parentLabel, ...ecoTaxLabels];
  }, [ecoTaxStepsCount, intl, isSuccess, ecoTaxData]);

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<FormValues>({
    values: {
      parent: {
        quantity: isEditMode ? item.quantity : '',
        unitPrice: isEditMode ? item.unitPrice : '',
        totalPrice: isEditMode ? item.totalPrice : '',
      },
      ecoTaxItems: isEditMode && item.ecoTaxItems && item.ecoTaxItems.length > 0
        ? item.ecoTaxItems
        : Array.from({ length: ecoTaxStepsCount }, () => ({
            quantity: '',
            unitPrice: '',
            totalPrice: '',
          })
        ),
      },
    mode: 'onBlur',
  });

  const handleCloseDialog = useCallback(() => {
    dispatch(clearSelectedItem());
    setActiveStep(0);
    reset();
  }, [dispatch, reset, setActiveStep]);

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    console.log({data})
    const correlationId = crypto.randomUUID();

    const payload: DialogFormData = {
      ...item,
      ...data.parent,
      ...(isSuccess && { correlationId }),
      // @ts-ignore allow undefined
      ecoTaxItems: data.ecoTaxItems.map((ecoTaxItem, index) => ({
        ...ecoTaxItem,
        ...(isSuccess && Array.isArray(ecoTaxData) ? ecoTaxData[index] : {}),
        ...(isSuccess && { correlationId }),
      })),
    };
    if (!isEditMode) {
      onAppendItem(payload);
      reset();
    } else if (itemIndex !== null) {
      onEditItem(payload, itemIndex);
    }
    handleCloseDialog();
  };

  const ecoTaxItemsValues = watch('ecoTaxItems');

  const allEcoTaxFieldsFilled =
    ecoTaxStepsCount === 0 ||
    ecoTaxItemsValues.every(
      (field) =>
        field.quantity !== '' &&
        field.unitPrice !== '' &&
        field.totalPrice !== ''
    );

  if (!item) return null;

  return (
    <Dialog
      component="form"
      open
      onClose={handleCloseDialog}
      onSubmit={handleSubmit(onSubmit)}
      sx={styles.dialog}
    >
      <DialogTitle align="center" sx={{ color: 'primary.main' }}>
        {isEditMode
          ? intl.formatMessage({ id: 'receipt.new.edit.item' })
          : intl.formatMessage({ id: 'receipt.new.add.item' })}
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <StepperComponent
          steps={steps} 
          activeStep={activeStep}
          onStepChange={goToStep}
        />
        {activeStep === 0 && (
          <ItemFormStep
            prefix="parent"
            control={control}
            errors={errors}
            intl={intl}
            parentItem={item}
            isParent
          />
        )}
        {activeStep > 0 && (
          <>
            <EcoTaxLoadingPlaceholder
              isLoading={isLoading} 
              isError={isError}
              intl={intl}
            />
            {isSuccess && Array.isArray(ecoTaxData) && (
              <ItemFormStep
                key={`ecoTaxItems-${activeStep - 1}`}
                prefix={`ecoTaxItems.${activeStep - 1}`}
                control={control}
                errors={errors}
                intl={intl}
                parentItem={item}
                ecoTaxData={ecoTaxData[activeStep - 1]}
                isParent={false}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Button onClick={handleCloseDialog} sx={styles.button}>
          {intl.formatMessage({ id: 'receipt.new.item.discard' })}
        </Button>
        {ecoTaxStepsCount > 0 && activeStep === 0 && (
          // @ts-ignore outlined prop valid in MUI
          <Button variant='outlined' onClick={() => goToStep(1)} sx={styles.button}>
            {intl.formatMessage({ id: 'receipt.fill.ecoTax' })}
          </Button>
        )}
        {ecoTaxStepsCount > 0 && activeStep > 0 && (
          <>
            <Button onClick={() => goToStep(0)} sx={styles.button}>
              {intl.formatMessage({ id: 'receipt.complete.step' })}
            </Button>
            {activeStep < ecoTaxStepsCount && (
              <Button onClick={() => goToStep(activeStep + 1)} sx={styles.button}>
                {intl.formatMessage({ id: 'receipt.next.step' })}
              </Button>
            )}
          </>
        )}
        {activeStep === 0 && (
          <Button variant="contained" type="submit" disabled={!allEcoTaxFieldsFilled} sx={styles.button}>
            {isEditMode
              ? intl.formatMessage({ id: 'receipt.new.edit.item.save' })
              : intl.formatMessage({ id: 'receipt.new.add.item.save' })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

